/**
 * Breadcrumb Component
 * Dynamic breadcrumb based on current route with action buttons slot
 * Phase 1: UI Architecture Restructure
 */

import { Link, useLocation } from 'react-router-dom'
import { ChevronRight, House } from 'lucide-react'
import type { ReactNode } from 'react'
import { useBreadcrumbOverride } from './BreadcrumbContext'
import { usePermissions } from '@/hooks/usePermissions'
import { resolveHomeRoute } from '@/router/RoleLanding'

// Route titles — used for breadcrumb labels
const routeTitles: Record<string, string> = {
  // General
  '/dashboard': 'Dashboard',
  '/change-password': 'Change Password',

  // Resource-based routes
  '/cases': 'Cases',
  '/cases/new': 'Create Proposal',
  '/policies': 'Policies',
  '/policies/calendar': 'Premium Calendar',
  '/commission': 'Commission',
  '/commission/history': 'Commission History',
  '/clients': 'Clients',

  // Legacy advisor paths (kept for breadcrumb if accessed via old URLs)
  '/advisor': 'Dashboard',
  '/advisor/commission': 'Commission',
  '/advisor/clients': 'Clients',
}

interface BreadcrumbProps {
  /** Optional action buttons to display on the right */
  actions?: ReactNode
}

export function Breadcrumb({ actions }: BreadcrumbProps) {
  const location = useLocation()
  const { override } = useBreadcrumbOverride()
  const { permissionSet } = usePermissions()
  const homePath = resolveHomeRoute(permissionSet ?? new Set<string>())

  // Use context override if provided, otherwise build from URL path
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const breadcrumbItems = override ?? buildBreadcrumbItems(pathSegments)

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      {/* Breadcrumb + Title */}
      <div className="space-y-1">
        {/* Breadcrumb navigation */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm">
          {/* Home link */}
          <Link
            to={homePath}
            className="flex items-center gap-1 text-foreground-muted hover:text-foreground transition-colors"
          >
            <House className="h-4 w-4" />
            <span className="sr-only">Home</span>
          </Link>

          {/* Breadcrumb items */}
          {breadcrumbItems.map((item, index) => {
            const href = 'href' in item ? item.href : (item as { path: string; label: string }).path
            return (
              <div key={href ?? item.label} className="flex items-center gap-1">
                <ChevronRight className="h-4 w-4 text-foreground-subtle" />
                {index === breadcrumbItems.length - 1 || !href ? (
                  // Current page (not a link) or no href
                  <span className="text-foreground font-medium">{item.label}</span>
                ) : (
                  // Parent pages (links)
                  <Link
                    to={href}
                    className="text-foreground-muted hover:text-foreground transition-colors"
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            )
          })}
        </nav>

        {/* Page title — intentionally omitted: breadcrumb nav provides sufficient context */}
      </div>

      {/* Action buttons slot */}
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  )
}

// Helper: Build breadcrumb items from path segments
function buildBreadcrumbItems(
  segments: string[]
): { href: string; label: string }[] {
  const items: { href: string; label: string }[] = []
  let accumulatedPath: string

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i] as string
    accumulatedPath = `/${segments.slice(0, i + 1).join('/')}`

    // Skip UUID-like segments entirely (they don't have dedicated pages)
    const isUuid = /^[0-9a-f-]{36}$/i.test(segment)
    if (isUuid) {
      continue
    }

    // Get label from routeTitles or format the segment
    const label = routeTitles[accumulatedPath] || formatSegment(segment)

    items.push({ href: accumulatedPath, label })
  }

  return items
}

// Helper: Format a path segment into a readable label
function formatSegment(segment: string): string {
  // Handle common segments
  const segmentMap: Record<string, string> = {
    users: 'Users',
    roles: 'Roles',
    create: 'Create',
    edit: 'Edit',
    dashboard: 'Dashboard',
    settings: 'Settings',
  }

  if (segmentMap[segment]) {
    return segmentMap[segment]
  }

  // Default: capitalize first letter of each word
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export default Breadcrumb
